import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom'
import { useRef, useEffect, useState } from 'react'
import Layout from './components/layout/Layout'
import AdminLayout from './components/layout/AdminLayout'
import Dashboard from './pages/Dashboard'
import Scans from './pages/Scans'
import Analytics from './pages/Analytics'
import Team from './pages/Team'
import Integrations from './pages/Integrations'
import WordPressGuide from './pages/WordPressGuide'
import WebsiteConnections from './pages/WebsiteConnections'
import ApiManagement from './pages/ApiManagement'
import Billing from './pages/Billing'
import Settings from './pages/Settings'
import Login from './pages/Login'
import ForgotPassword from './pages/ForgotPassword'
import ResetPassword from './pages/ResetPassword'
import Plans from './pages/Plans'
import Register from './pages/Register'
import Checkout from './pages/Checkout'
import Notifications from './pages/Notifications'
import WebsiteScanHistory from './pages/WebsiteScanHistory'
import Compliance from './pages/Compliance'
import ScanPlannerPage from './pages/ScanPlannerPage'
import SuperAdminDashboard from './pages/admin/SuperAdminDashboard'
import CompaniesAndUsers from './pages/admin/CompaniesAndUsers'
import SubscriptionAndBillings from './pages/admin/SubscriptionAndBillings'
import SubscriptionPlans from './pages/admin/SubscriptionPlans'
import ProfileSettings from './pages/admin/ProfileSettings'
import useAuthStore from './store/useAuthStore'
import LoadingSpinner from './components/common/LoadingSpinner'
import ProtectedRoutes from './components/common/ProtectedRoutes'

const getDefaultRouteByRole = (role) => {
  if (role === 'superAdmin') return '/admin/dashboard'
  if (role === 'teamMember') return '/integrations'
  return '/dashboard'
}

function LandingRoute() {
  const { isAuthenticated, user } = useAuthStore()
  return isAuthenticated ? <Navigate to={getDefaultRouteByRole(user?.role)} replace /> : <Navigate to="/login" replace />
}

function ProtectedRoute() {
  const { isAuthenticated, getMe, logout } = useAuthStore()
  const [isLoading, setIsLoading] = useState(true)
  const accessToken = localStorage.getItem('accessToken')
  const hasValidated = useRef(false)

  useEffect(() => {
    const validateToken = async () => {
      if (accessToken && isAuthenticated && !hasValidated.current) {
        hasValidated.current = true
        try {
          await getMe()
        } catch (error) {
          console.error('Token validation failed:', error)
          await logout()
          hasValidated.current = false
        }
      }
      setIsLoading(false)
    }

    validateToken()
  }, [accessToken, isAuthenticated, getMe, logout])

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner />
      </div>
    )
  }

  return isAuthenticated ? <Layout><Outlet /></Layout> : <Navigate to="/login" replace />
}

function NonTeamMemberRoute() {
  const { user } = useAuthStore()

  if (user?.role === 'teamMember') {
    return <Navigate to="/integrations" replace />
  }

  return <Outlet />
}

function RoleRoute({ allowedRoles }) {
  const { user } = useAuthStore()

  if (allowedRoles && !allowedRoles.includes(user?.role)) {
    return <Navigate to={getDefaultRouteByRole(user?.role)} replace />
  }

  return <Outlet />
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<LandingRoute />} />
        <Route path="/login" element={<Login />} />
        <Route path="/plans" element={<Plans />} />
        <Route path="/register" element={<Register />} />
        <Route path="/checkout" element={<Checkout />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />

        {/* Protected Routes - All wrapped in ProtectedRoute */}
        <Route element={<ProtectedRoute />}>
          <Route element={<RoleRoute allowedRoles={['companyAdmin', 'teamMember']} />}>
            <Route path="/integrations" element={<Integrations />} />
            <Route path="/integrations/wordpress-guide" element={<WordPressGuide />} />
            <Route path="/scan-planner" element={<ScanPlannerPage />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/website-scans/:websiteId" element={<WebsiteScanHistory />} />
          </Route>

          <Route element={<RoleRoute allowedRoles={['companyAdmin']} />}>
            <Route element={<NonTeamMemberRoute />}>
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/scans" element={<Scans />} />
              <Route path="/analytics" element={<Analytics />} />
              <Route path="/team" element={<Team />} />
              <Route path="/website-connections" element={<WebsiteConnections />} />
              <Route path="/api-management" element={<ApiManagement />} />
              <Route path="/billing" element={<Billing />} />
              <Route path="/notifications" element={<Notifications />} />
              <Route path="/compliance" element={<Compliance />} />
            </Route>
          </Route>
        </Route>

        {/* Super Admin Routes */}
        <Route
          element={
            <ProtectedRoutes allowedRoles={["superAdmin"]}>
              <AdminLayout />
            </ProtectedRoutes>
          }
        >
          <Route path="/admin/dashboard" element={<SuperAdminDashboard />} />
          <Route path="/admin/companies-and-users" element={<CompaniesAndUsers />} />
          <Route path="/admin/subscription-and-billings" element={<SubscriptionAndBillings />} />
          <Route path="/admin/subscription-plans" element={<SubscriptionPlans />} />
          <Route path="/admin/profile-setting" element={<ProfileSettings />} />
        </Route>

        {/* Redirect unknown routes */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  )
}

export default App
